import NiceModal from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { ACTION_OBJECT_TYPES } from 'actions/useActionsLibrary';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import ProgressWizard from 'components/field/ProgressWizard';
import { MobileWizardContainer } from 'components/field/styled/ProgressWizard.styled';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import ConfirmationDialog from 'components/global/ConfirmationDialog';
import FormHeader from 'components/global/FormHeader';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';
import { HeaderContainerFormHeader } from 'components/global/styled/FormHeader.styled';
import DynamicList from 'components/list/DynamicList';
import { ButtonContainer } from 'components/list/DynamicListFilter.styled';
import { FilterAction, FilterType, ListComponents } from 'components/list/DynamicListFilterUtils';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import { usePrompt } from 'hooks/usePrompt';

import ImportDataModal from 'modals/ImportDataModal';

import BulletDistrictSLAForm from 'pages/PriceConfigurations/BulletDistrictSLA/BulletDistrictSLAForm';
import BulletKMSLAForm from 'pages/PriceConfigurations/BulletKMSLA/BulletKMSLAForm';
import CityBasedSLAForm from 'pages/PriceConfigurations/CityBasedSLA/CityBasedSLAForm';
import CountryBasedSLAForm from 'pages/PriceConfigurations/CountryBasedSLA/CountryBasedSLAForm';
import DistrictBasedSLAForm from 'pages/PriceConfigurations/DistrictBasedSLA/DistrictBasedSLAForm';
import KMBasedSLAForm from 'pages/PriceConfigurations/KMBasedSLA/KMBasedSLAForm';
import TierBasedSLAForm from 'pages/PriceConfigurations/TierBasedSLA/TierBasedSLAForm';
import { importActions } from 'pages/Settings/ImportExport/ImportLog/ImportLogList';

import { ENUMS, SHIPPING_PARTNERS } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';
import PATHS, { mergePaths } from 'utils/paths';

import {
	faAngleLeft,
	faAngleRight,
	faArrowLeft,
	faDownload,
	faFloppyDisk,
	faKeySkeletonLeftRight,
	faPlus,
	faScrewdriverWrench
} from '@fortawesome/pro-solid-svg-icons';

import * as S from './ConnectedPartners.styled';

/**
 * @author @muhammedburakak
 */

export const SLAConfigurationForm = () => {
	const { t } = useTranslation();
	const axios = useAxiosPrivate();
	const navigate = useNavigate();
	const { state } = useLocation();
	const { showSuccess, showError } = useAlert();

	const { settingsID, icon, integrationName, name, subname } = state;

	const [isLoading, setIsLoading] = useState(false);
	const [selectedIndex, setSelectedIndex] = useState(0);
	const [submitButtonRef, setSubmitButtonRef] = useState(null);
	const [slaTypeFirstTime, setSlaTypeFirstTime] = useState(null);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					slaMethodType: yup.string().nullable().required()
				})
				.required(),

		[]
	);

	// Form setup using react-hook-form
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			slaMethodType: null
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitSuccessful, isSubmitting, errors }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const fillForm = useCallback(async () => {
		setIsLoading(true);
		await axios
			.post(SHIPPING_PARTNERS.GET_SHIPPING_PARTNER_SLA_METHOD, {
				deliveryCompanySettingsId: settingsID,
				deliveryCompanySettingsName: integrationName
			})
			.then((response) => {
				const { data, status } = response;

				if (status === 200) {
					reset({
						slaMethodType: data.slaMethodType || null,
						contractID: data.id || null
					});

					if (data.slaMethodType) {
						nextStep();
					}

					setSlaTypeFirstTime(data.slaMethodType || null);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [settingsID]);

	useEffect(() => {
		if (settingsID) fillForm();
	}, [settingsID]);

	const onSubmit = async (data) => {
		await axios
			.post(SHIPPING_PARTNERS.UPDATE_SHIPPING_PARTNER_SLA_METHOD, {
				deliveryCompanySettingsId: settingsID,
				slaMethodType: data.slaMethodType
			})
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess({ message: 'slaMethodUpdated' });
					reset({
						slaMethodType: data.slaMethodType,
						contractID: data.contractID
					});
					setSlaTypeFirstTime(data.slaMethodType);
					nextStep();
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});
	};

	const prevStep = () => {
		setSelectedIndex(selectedIndex - 1);
	};

	const nextStep = () => {
		setSelectedIndex(selectedIndex + 1);
	};

	return (
		<div>
			<HeaderContainerFormHeader>
				<FormHeader lightTitle={name} boldTitle="getLocation" icon={faArrowLeft} />

				<ProgressWizard
					className="progressWizardFullscreen"
					stages={[
						{ name: 'slaMethod', icon: faKeySkeletonLeftRight },
						{ name: 'slaConfigurationDetails', icon: faScrewdriverWrench }
					]}
					setIndex={setSelectedIndex}
					index={selectedIndex}
				/>

				<ButtonContainer>
					{selectedIndex === 0 && (
						<Button
							label="cancel"
							variant="outline"
							onClick={() => {
								navigate(mergePaths(PATHS.SHIPPING_PARTNERS, PATHS.CONNECTED_PARTNERS));
							}}
						/>
					)}

					{selectedIndex === 1 && (
						<Button
							label="previous"
							variant="outline"
							icon={document.body.dir === 'rtl' ? faAngleRight : faAngleLeft}
							onClick={prevStep}
						/>
					)}

					{selectedIndex === 0 && (
						<Button
							ref={isDirty && !errors.slaMethodType ? setSubmitButtonRef : null}
							icon={document.body.dir === 'rtl' ? faAngleLeft : faAngleRight}
							label="continue"
							iconPos="right"
							disabled={errors.slaMethodType}
							disabledTooltip={errors.slaMethodType && 'errorMsg.youMustSelectAnOption'}
							variant="blue"
							loading={isLoading}
							onClick={
								!isDirty
									? handleSubmit(nextStep, (errors) => {
											AppLogger.log(errors);
										})
									: undefined
							}
						/>
					)}

					<ConfirmationDialog
						buttonRef={submitButtonRef}
						placement="bottom-end"
						title={slaTypeFirstTime ? t('slaMethodChangeTitle') : t('slaMethodAddTitle')}
						subTitle={slaTypeFirstTime ? t('slaMethodChangeText') : t('slaMethodAddText')}
						rightLabel="save"
						leftLabel="cancel"
						rightIcon={faFloppyDisk}
						rightAction={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				</ButtonContainer>
			</HeaderContainerFormHeader>

			<MobileWizardContainer>
				<ProgressWizard
					className="progressWizardFullscreen"
					stages={[
						{ name: 'slaMethod', icon: faKeySkeletonLeftRight },
						{ name: 'slaConfigurationDetails', icon: faScrewdriverWrench }
					]}
					setIndex={setSelectedIndex}
					index={selectedIndex}
				/>
			</MobileWizardContainer>

			<Form formMethods={formMethods}>
				<ShippingFormHeader
					settingsID={settingsID}
					icon={icon}
					integrationName={integrationName}
					name={name}
					watch={watch}
					subname={subname}
					tab={selectedIndex}
				/>

				<div className="w-100">
					{selectedIndex === 0 && (
						<FormItem label={'slaMethodType'} required>
							<EnumSelect
								valueRemovable={false}
								searchable={false}
								name="slaMethodType"
								enumName={ENUMS.USER_SLA_METHOD_TYPE}
								setValue={(value) => {
									setValue(`slaMethodType`, value, { shouldValidate: true, shouldDirty: true });
								}}
								value={watch(`slaMethodType`)}
							/>
						</FormItem>
					)}

					{selectedIndex === 1 && <ListToShow settingsID={settingsID} watch={watch} />}
				</div>
			</Form>
		</div>
	);
};

export default SLAConfigurationForm;

// lists

const ListToShow = ({ settingsID, watch }) =>
	useMemo(() => {
		if (watch('slaMethodType') === 'KM_BASED') return <KMBasedSLAList settingsID={settingsID} />;
		if (watch('slaMethodType') === 'CITY_BASED') return <CityBasedSLAList settingsID={settingsID} />;
		if (watch('slaMethodType') === 'TIER_BASED') return <TierBasedSLAList settingsID={settingsID} />;
		if (watch('slaMethodType') === 'DISTRICT_BASED') return <DistrictBasedSLAList settingsID={settingsID} />;
		if (watch('slaMethodType') === 'BULLET_KM') return <DistanceBasedBulletKMSLA settingsID={settingsID} />;
		if (watch('slaMethodType') === 'COUNTRY_BASED') return <CountryBasedSLAList settingsID={settingsID} />;
		if (watch('slaMethodType') === 'BULLET_DISTRICT') return <BulletDistrictSLAList settingsID={settingsID} />;
	}, [settingsID]);

const CityBasedSLAList = ({ settingsID }) => {
	const cityBasedColumns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'deliveryCompany',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'country',
				accessor: 'country',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'originCity',
				accessor: 'originCity',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'destinationCity',
				accessor: 'destinationCity',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'cutOffSameDayPickup',
				accessor: 'cutOffSameDayPickup',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'pickupDaysOfWeek',
				accessor: 'pickupDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},
			{
				Header: 'deliveryDaysOfWeek',
				accessor: 'deliveryDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},
			{
				Header: 'transferDuration',
				accessor: 'transferDuration',
				filterType: FilterType.string,
				width: 100
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					accessor: 'deliveryCompanySettingsId',
					Header: 'deliveryCompanySettingsId',
					filterType: FilterType.number
				},
				action: FilterAction.contains,
				value: settingsID
			}
		],
		[settingsID]
	);

	return (
		<div>
			<Alert content="cityBasedSlaInfoText" className="m-3" showIcon />

			<DynamicList
				id={DYNAMICLISTS.SHIPPING_PARTNERS_CITY_BASED_SLA}
				showFilters={false}
				defaultFilters={defaultFilters}
				columns={cityBasedColumns}
				fetchUrl={SHIPPING_PARTNERS.GET_CITY_SLA_METHOD_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="cityBasedSLA"
				showColumnCalculation={false}
			/>
		</div>
	);
};

const DistanceBasedBulletKMSLA = ({ settingsID }) => {
	const distanceBasedBulletKMColumns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'deliveryCompany',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'greaterThanKm',
				accessor: 'greaterThanKm',
				filterType: FilterType.float,
				width: 100
			},
			{
				Header: 'lessThanOrEqualKm',
				accessor: 'lessThanOrEqualKm',
				filterType: FilterType.float,
				width: 100
			},
			{
				Header: 'pickupSla',
				accessor: 'pickupSla',
				filterType: FilterType.float,
				width: 100
			},
			{
				Header: 'deliverSla',
				accessor: 'deliverSla',
				filterType: FilterType.float,
				width: 100
			},
			{
				Header: 'totalDeliveryCutOff',
				accessor: 'totalDeliveryCutOff',
				filterType: FilterType.float,
				width: 100
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					accessor: 'deliveryCompanySettingsId',
					Header: 'deliveryCompanySettingsId',
					filterType: FilterType.number
				},
				action: FilterAction.contains,
				value: settingsID
			}
		],
		[settingsID]
	);

	return (
		<div>
			<Alert content="bulletKMSlaInfoText" className="m-3" showIcon />

			<DynamicList
				id={DYNAMICLISTS.SHIPPING_PARTNERS_BULLET_KM_SLA}
				showFilters={false}
				defaultFilters={defaultFilters}
				columns={distanceBasedBulletKMColumns}
				fetchUrl={SHIPPING_PARTNERS.GET_BULLET_KM_SLA_METHOD_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="bulletKMSLA"
				showColumnCalculation={false}
			/>
		</div>
	);
};

const KMBasedSLAList = ({ settingsID }) => {
	const kmBasedColumns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'deliveryCompany',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'greaterThanKm',
				accessor: 'greaterThanKm',
				filterType: FilterType.number,
				width: 80
			},
			{
				Header: 'lessThanOrEqualKm',
				accessor: 'lessThanOrEqualKm',
				filterType: FilterType.number,
				width: 80
			},
			{
				Header: 'cutOffSameDayPickup',
				accessor: 'cutOffSameDayPickup',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'pickupDaysOfWeek',
				accessor: 'pickupDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},
			{
				Header: 'deliveryDaysOfWeek',
				accessor: 'deliveryDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},
			{
				Header: 'transferDuration',
				accessor: 'transferDuration',
				filterType: FilterType.number,
				width: 100
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					accessor: 'deliveryCompanySettingsId',
					Header: 'deliveryCompanySettingsId',
					filterType: FilterType.number
				},
				action: FilterAction.contains,
				value: settingsID
			}
		],
		[settingsID]
	);

	return (
		<div>
			<Alert content="kmBasedSlaInfoText" className="m-3" showIcon />

			<DynamicList
				id={DYNAMICLISTS.SHIPPING_PARTNERS_KM_BASED_SLA}
				showFilters={false}
				defaultFilters={defaultFilters}
				columns={kmBasedColumns}
				fetchUrl={SHIPPING_PARTNERS.GET_KM_SLA_METHOD_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="kmBasedSLA"
				showColumnCalculation={false}
			/>
		</div>
	);
};

const TierBasedSLAList = ({ settingsID }) => {
	const tierBasedColumns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'deliveryCompany',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'cutOffSameDayPickup',
				accessor: 'cutOffSameDayPickup',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'transferDuration',
				accessor: 'transferDuration',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'originTier',
				accessor: 'originTier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_TIER_TYPE,
				width: 100
			},
			{
				Header: 'destinationTier',
				accessor: 'destinationTier',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.PRICE_CONFIGURATION_TIER_TYPE,
				width: 100
			},

			{
				Header: 'pickupDaysOfWeek',
				accessor: 'pickupDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},

			{
				Header: 'deliveryDaysOfWeek',
				accessor: 'deliveryDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					accessor: 'deliveryCompanySettingsId',
					Header: 'deliveryCompanySettingsId',
					filterType: FilterType.number
				},
				action: FilterAction.contains,
				value: settingsID
			}
		],
		[settingsID]
	);

	return (
		<div>
			<Alert content="tierBasedSlaInfoText" className="m-3" showIcon />

			<DynamicList
				id={DYNAMICLISTS.SHIPPING_PARTNERS_TIER_BASED_SLA}
				showFilters={false}
				defaultFilters={defaultFilters}
				columns={tierBasedColumns}
				fetchUrl={SHIPPING_PARTNERS.GET_TIER_SLA_METHOD_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="tierBasedSLA"
				showColumnCalculation={false}
			/>
		</div>
	);
};

const DistrictBasedSLAList = ({ settingsID }) => {
	const districtBasedColumns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'deliveryCompany',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'country',
				accessor: 'country',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'destinationCity',
				accessor: 'destinationCity',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'destinationDistrict',
				accessor: 'destinationDistrict',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'cutOffSameDayPickup',
				accessor: 'cutOffSameDayPickup',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'pickupDaysOfWeek',
				accessor: 'pickupDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},
			{
				Header: 'deliveryDaysOfWeek',
				accessor: 'deliveryDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},
			{
				Header: 'transferDuration',
				accessor: 'transferDuration',
				filterType: FilterType.string,
				width: 100
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					accessor: 'deliveryCompanySettingsId',
					Header: 'deliveryCompanySettingsId',
					filterType: FilterType.number
				},
				action: FilterAction.contains,
				value: settingsID
			}
		],
		[settingsID]
	);

	return (
		<div>
			<Alert content="districtBasedSlaInfoText" className="m-3" showIcon />

			<DynamicList
				id={DYNAMICLISTS.SHIPPING_PARTNERS_DISTRICT_BASED_SLA}
				showFilters={false}
				defaultFilters={defaultFilters}
				columns={districtBasedColumns}
				fetchUrl={SHIPPING_PARTNERS.GET_DISTRICT_SLA_METHOD_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="districtBasedSLA"
				showColumnCalculation={false}
			/>
		</div>
	);
};

const CountryBasedSLAList = ({ settingsID }) => {
	const countryBasedColumns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'deliveryCompany',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'cutOffSameDayPickup',
				accessor: 'cutOffSameDayPickup',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'transferDuration',
				accessor: 'transferDuration',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'originCountry',
				accessor: 'originCountry',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'destinationCountry',
				accessor: 'destinationCountry',
				filterType: FilterType.string,
				width: 100
			},

			{
				Header: 'pickupDaysOfWeek',
				accessor: 'pickupDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			},

			{
				Header: 'deliveryDaysOfWeek',
				accessor: 'deliveryDaysOfWeek',
				filterType: FilterType.enumMulti,
				renderCell: ListComponents.TEXT_ARRAY,
				enumName: ENUMS.PRICE_CONFIGURATION_DAYS,
				width: 100
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					accessor: 'deliveryCompanySettingsId',
					Header: 'deliveryCompanySettingsId',
					filterType: FilterType.number
				},
				action: FilterAction.contains,
				value: settingsID
			}
		],
		[settingsID]
	);

	return (
		<div>
			<Alert content="countryBasedSlaInfoText" className="m-3" showIcon />
			<DynamicList
				id={DYNAMICLISTS.SHIPPING_PARTNERS_COUNTRY_BASED_SLA}
				showFilters={false}
				defaultFilters={defaultFilters}
				columns={countryBasedColumns}
				fetchUrl={SHIPPING_PARTNERS.GET_COUNTRY_SLA_METHOD_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="countryBasedSLA"
				showColumnCalculation={false}
			/>
		</div>
	);
};

const BulletDistrictSLAList = ({ settingsID }) => {
	const bulletDistrictColumns = useMemo(
		() => [
			{ Header: 'ID', accessor: 'id', filterType: FilterType.number, width: 70, show: false },
			{
				Header: 'deliveryCompany',
				accessor: 'shippingContractName',
				filterType: FilterType.string,
				width: 100
			},
			{
				Header: 'pickupSla',
				accessor: 'pickupSla',
				filterType: FilterType.float,
				width: 100
			},
			{
				Header: 'deliverSla',
				accessor: 'deliverSla',
				filterType: FilterType.float,
				width: 100
			},
			{
				Header: 'totalDeliveryCutOff',
				accessor: 'totalDeliveryCutOff',
				filterType: FilterType.float,
				width: 100
			},
			{
				Header: 'originCity',
				accessor: 'originCity',
				filterType: FilterType.string
			},
			{
				Header: 'originCountry',
				accessor: 'originCountry',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY
			},
			{
				Header: 'originDistrict',
				accessor: 'originDistrict',
				filterType: FilterType.string
			},
			{
				Header: 'destinationCity',
				accessor: 'destinationCity',
				filterType: FilterType.string,
				showOnMobile: true,
				width: 100
			},
			{
				Header: 'destinationCountry',
				accessor: 'destinationCountry',
				filterType: FilterType.enumMulti,
				enumName: ENUMS.COUNTRIES,
				filterPrefix: 'country.',
				renderCell: ListComponents.COUNTRY,
				width: 100
			},

			{
				Header: 'destinationDistrict',
				accessor: 'destinationDistrict',
				filterType: FilterType.string,
				showOnMobile: true,
				width: 100
			}
		],
		[]
	);

	const defaultFilters = useMemo(
		() => [
			{
				column: {
					accessor: 'deliveryCompanySettingsId',
					Header: 'deliveryCompanySettingsId',
					filterType: FilterType.number
				},
				action: FilterAction.contains,
				value: settingsID
			}
		],
		[settingsID]
	);

	return (
		<div>
			<Alert content="bulletDistrictSlaInfoText" className="m-3" showIcon />

			<DynamicList
				id={DYNAMICLISTS.SHIPPING_PARTNERS_BULLET_DISTRICT_SLA}
				showFilters={false}
				defaultFilters={defaultFilters}
				columns={bulletDistrictColumns}
				fetchUrl={SHIPPING_PARTNERS.GET_BULLET_DISTRICT_SLA_METHOD_LIST}
				actionObjectType={ACTION_OBJECT_TYPES.OTHER}
				exportName="bulletDistrictSLA"
				showColumnCalculation
			/>
		</div>
	);
};

const ButtonReturnFunc = ({ settingsID, slaMethodType, contractID }) => {
	const importData = (importAction) => {
		NiceModal.show(ImportDataModal, importAction);
	};

	let importAction, SLAFormLabel, form;
	switch (slaMethodType) {
		case 'KM_BASED':
			importAction = importActions.kmBasedPriceImportForCompany;
			SLAFormLabel = 'addKMBasedSLA';
			form = KMBasedSLAForm;
			break;
		case 'CITY_BASED':
			importAction = importActions.cityBasedPriceImportForCompany;
			SLAFormLabel = 'addCityBasedSLA';
			form = CityBasedSLAForm;
			break;
		case 'TIER_BASED':
			importAction = importActions.tierBasedSLAImportForCompany;
			SLAFormLabel = 'addTierBasedSLA';
			form = TierBasedSLAForm;
			break;
		case 'DISTRICT_BASED':
			importAction = importActions.districtBasedSLAImportForCompany;
			SLAFormLabel = 'addDistrictBasedSLA';
			form = DistrictBasedSLAForm;
			break;
		case 'BULLET_KM':
			importAction = importActions.bulletKMSLAImportForCompany;
			SLAFormLabel = 'addBulletKMSLA';
			form = BulletKMSLAForm;
			break;
		case 'BULLET_DISTRICT':
			importAction = importActions.bulletDistrictSLAImportForCompany;
			SLAFormLabel = 'addBulletDistrictSLA';
			form = BulletDistrictSLAForm;
			break;
		case 'COUNTRY_BASED':
			importAction = importActions.countryBasedSLAImportForCompany;
			SLAFormLabel = 'addCountryBasedSLA';
			form = CountryBasedSLAForm;
			break;
		default:
			break;
	}

	return (
		<>
			<Button
				className="mobile-hidden importDataButton"
				icon={faDownload}
				variant="borderless"
				label="importData"
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/importData-icon.json.gz"
				onClick={() => importData(importAction)}
			/>
			<Button
				icon={faPlus}
				lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/outline/addOrder-icon.json.gz"
				label={SLAFormLabel}
				onClick={() => NiceModal.show(form, { settingsID, contractID })}
				variant="blue"
				className="addButton"
			/>
		</>
	);
};

const ShippingFormHeader = ({ icon, integrationName, name, tab, watch, subname, settingsID }) => {
	const slaMethodType = watch('slaMethodType');
	const contractID = watch('contractID');

	return (
		<>
			<S.RowSpaceSla>
				<S.Header className="p-3">
					<Image alt={`${integrationName}_logo`} url={icon} width="7rem" height="7rem" />
					<div>
						<Typography variant="p2" fontWeight="500">
							{name}
						</Typography>
						<Typography noWrap>{subname}</Typography>
					</div>
				</S.Header>

				{tab === 1 && (
					<S.RowCenteredSla>
						<ButtonReturnFunc
							settingsID={settingsID}
							slaMethodType={slaMethodType}
							contractID={contractID}
						/>
					</S.RowCenteredSla>
				)}
			</S.RowSpaceSla>

			{tab === 0 && <Alert content="slaMethodInfoText" className="m-3" showIcon />}
		</>
	);
};
